/**
 * Prize Helper Functions
 * Utilities for selecting and working with prize data.
 */

/**
 * Normalise a raw prize object from the API so downstream code can always
 * use snake_case `image_url`.  The API may return camelCase `imageUrl`.
 *
 * @param {Object} prize - Raw prize from API
 * @returns {Object} - Prize with image_url normalised
 */
export function normalizePrize(prize) {
  if (!prize || typeof prize !== 'object') return prize;
  return {
    ...prize,
    image_url: prize.image_url || prize.imageUrl || null,
  };
}

/**
 * Normalise an array of raw prizes.
 *
 * @param {Array} prizes
 * @returns {Array}
 */
export function normalizePrizes(prizes) {
  if (!Array.isArray(prizes)) return [];
  return prizes.map(normalizePrize);
}

/**
 * Get the image URL from a prize object, handling both API casing variants.
 *
 * @param {Object|null} prize
 * @returns {string|null}
 */
export function getPrizeImageUrl(prize) {
  if (!prize) return null;
  return prize.image_url || prize.imageUrl || null;
}

/**
 * Get the hero prize based on priority: grand → first → second.
 * Returns the best-matching prize regardless of whether it has an image;
 * the caller decides how to render when no image is available.
 *
 * @param {Array} prizes - Array of prize objects (already normalised or raw)
 * @returns {Object|null} - The hero prize or null if none found
 */
export function getHeroPrize(prizes) {
  if (!Array.isArray(prizes) || prizes.length === 0) {
    return null;
  }

  // Priority order: grand → first → second → any
  const tierPriority = ['grand', 'first', 'second'];

  for (const tier of tierPriority) {
    // Prefer prizes that have an image first, then fall back to any in that tier
    const withImage = prizes.find(p => p.tier === tier && (p.image_url || p.imageUrl));
    if (withImage) return withImage;
    const withoutImage = prizes.find(p => p.tier === tier);
    if (withoutImage) return withoutImage;
  }

  // Fall back to the first prize in the list
  return prizes[0] || null;
}

/**
 * Resolve a prize field that may be a plain string or a localized object
 * (e.g. { English: "Car", French: "Voiture" }).
 *
 * Lookup order: exact language key → "English" → "en" → first available value.
 *
 * @param {string|object|null|undefined} field - The field value from the API
 * @param {string} [language='English'] - Current language key
 * @returns {string} Resolved string (never null/undefined)
 */
export function getLocalizedPrizeField(field, language = 'English') {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    return field[language] || field.English || field.en || Object.values(field)[0] || '';
  }
  return String(field);
}
